'use client'

import * as React from 'react'
import { cm } from '~/utils/utils'
import { ChevronDownIcon } from '../../usage-dashboard/icons'

interface InlineDropdownOption {
  id: string | number
  label: string
  isSelected?: boolean
  isSeparator?: boolean
  isLabel?: boolean
}

interface InlineDropdownProps {
  /** The trigger element text or custom render */
  trigger: React.ReactNode
  /** Options to display */
  options: InlineDropdownOption[]
  /** Called when an option is selected */
  onSelect: (option: InlineDropdownOption) => void
  /** Whether the dropdown is open (controlled) */
  isOpen?: boolean
  /** Callback when open state changes */
  onOpenChange?: (isOpen: boolean) => void
  /** Alignment of dropdown relative to trigger */
  align?: 'start' | 'end'
  /** Width of dropdown */
  width?: number
  /** Additional className for the dropdown container */
  className?: string
  /** Whether to allow multiple selections (checkbox style) */
  multiSelect?: boolean
  /** Footer content */
  footer?: React.ReactNode
}

/**
 * A simple inline dropdown that doesn't use portals.
 * Designed to work properly inside popovers without stacking issues.
 */
export function InlineDropdown({
  trigger,
  options,
  onSelect,
  isOpen: controlledIsOpen,
  onOpenChange,
  align = 'end',
  width = 400,
  className,
  multiSelect = false,
  footer
}: InlineDropdownProps) {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalOpen
  const setIsOpen = React.useCallback(
    (open: boolean) => {
      if (controlledIsOpen === undefined) {
        setInternalOpen(open)
      }
      onOpenChange?.(open)
    },
    [controlledIsOpen, onOpenChange]
  )

  // Close on click outside
  React.useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    // Use capture phase to get the event before it bubbles
    document.addEventListener('mousedown', handleClickOutside, true)
    return () => document.removeEventListener('mousedown', handleClickOutside, true)
  }, [isOpen, setIsOpen])

  // Close on escape
  React.useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, setIsOpen])

  return (
    <div ref={containerRef} className={cm('relative inline-block', className)}>
      {/* Trigger */}
      <button
        type='button'
        onClick={() => setIsOpen(!isOpen)}
        className='flex items-center gap-2 px-3 py-2 text-sm font-medium text-tokens-neutral-text-primary hover:bg-tokens-neutral-fill-fill-6 rounded-lg transition-colors'
      >
        {trigger}
        <span className={cm('transition-transform shrink-0', isOpen && 'rotate-180')}>
          <ChevronDownIcon />
        </span>
      </button>

      {/* Dropdown content */}
      {isOpen && (
        <div
          className={cm(
            'absolute z-50 -mt-8 overflow-hidden rounded-2xl bg-white shadow-popover border border-tokens-neutral-border-2',
            align === 'end' ? 'right-0' : 'left-0'
          )}
          style={{ width }}
        >
          <div className='max-h-[320px] overflow-y-auto p-2'>
            {options.map((option, index) => {
              if (option.isSeparator) {
                return (
                  <div key={`sep-${index}`} className='my-2'>
                    <svg
                      width='100%'
                      height='1'
                      viewBox='0 0 100 1'
                      fill='none'
                      preserveAspectRatio='none'
                    >
                      <path d='M0 0.5L100 0.5' stroke='#EAEAEA' vectorEffect='non-scaling-stroke' />
                    </svg>
                  </div>
                )
              }

              if (option.isLabel) {
                return (
                  <div
                    key={`label-${index}`}
                    className='px-3 py-1.5 text-xs font-medium text-tokens-neutral-text-tertiary font-mono'
                  >
                    {option.label}
                  </div>
                )
              }

              return (
                <button
                  key={option.id}
                  type='button'
                  onClick={e => {
                    if (multiSelect) {
                      e.preventDefault()
                    }
                    onSelect(option)
                    if (!multiSelect) {
                      setIsOpen(false)
                    }
                  }}
                  className={cm(
                    'relative flex w-full h-8 cursor-pointer select-none items-center justify-between',
                    'rounded-lg px-3 text-sm font-medium outline-none transition-colors',
                    'hover:bg-tokens-neutral-fill-fill-6',
                    option.isSelected && 'text-tokens-primary-2'
                  )}
                >
                  {option.label}
                  {option.isSelected && (
                    <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                      <path
                        d='M13.3332 4L5.99984 11.3333L2.6665 8'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </button>
              )
            })}
          </div>

          {/* Footer */}
          {footer && (
            <div className='border-t border-tokens-neutral-border-2 p-3 flex gap-2 justify-end bg-tokens-neutral-bg-grey-1'>
              {footer}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default InlineDropdown
